import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Database,
  Gauge,
  RefreshCw,
} from "lucide-react";
import type {
  RoutingGraphImportRunStatusViewModel,
  RoutingGraphImportStatusTone,
  RoutingGraphImportStatusViewProps,
} from "../../contracts/routing-graph-import-status";
import { Button } from "../../internal/primitives/button";
import { cn } from "../../lib/utils";

const toneClasses: Record<RoutingGraphImportStatusTone, string> = {
  neutral: "border-border bg-muted/40 text-muted-foreground",
  success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  warning: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  danger: "border-destructive/30 bg-destructive/10 text-destructive",
  info: "border-primary/30 bg-primary/10 text-primary",
};

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "Awaiting heartbeat";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3_600)}h ago`;
}

function formatRate(rate: number | null): string {
  if (rate === null) return "Calculating";
  return `${Math.round(rate).toLocaleString()} rows/s`;
}

function StatusBadge({ label, tone }: { label: string; tone: RoutingGraphImportStatusTone }) {
  return (
    <span className={cn("inline-flex rounded-control border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider", toneClasses[tone])}>
      {label}
    </span>
  );
}

function RunRow({ run }: { run: RoutingGraphImportRunStatusViewModel }) {
  const progress = run.progressPercent === null ? 0 : Math.max(0, Math.min(100, run.progressPercent));
  return (
    <article className="border-t border-border px-4 py-3 first:border-t-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-black text-foreground">{run.regionKey}</h3>
            <StatusBadge label={run.statusLabel} tone={run.statusTone} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{run.phaseLabel}{run.progressMessage ? ` · ${run.progressMessage}` : ""}</p>
        </div>
        <span className="font-mono text-xs font-bold text-foreground">{run.progressPercent === null ? "Working…" : `${Math.round(progress)}%`}</span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-label={`${run.regionKey} import progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={run.progressPercent === null ? undefined : Math.round(progress)}>
        <div className={cn("h-full bg-primary transition-[width]", run.progressPercent === null && "w-1/3 animate-pulse")} style={run.progressPercent === null ? undefined : { width: `${progress}%` }} />
      </div>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
        <div className="flex items-center gap-2"><Clock3 className="h-3.5 w-3.5 text-muted-foreground" /><div><dt className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Heartbeat</dt><dd className="font-bold text-foreground">{formatDuration(run.heartbeatAgeSeconds)}</dd></div></div>
        <div className="flex items-center gap-2"><Gauge className="h-3.5 w-3.5 text-muted-foreground" /><div><dt className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Throughput</dt><dd className="font-bold text-foreground">{formatRate(run.rowsPerSecond)}</dd></div></div>
        <div className="flex items-center gap-2"><Activity className="h-3.5 w-3.5 text-muted-foreground" /><div><dt className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Batch</dt><dd className="font-bold text-foreground">{run.currentBatch === null ? "Pending" : `${run.currentBatch}${run.totalBatches === null ? "" : ` / ${run.totalBatches}`}`}</dd></div></div>
      </dl>
      {run.error ? <p className="mt-3 flex items-start gap-2 text-xs font-medium text-destructive"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{run.error}</p> : null}
    </article>
  );
}

export function RoutingGraphImportStatusView({
  loading,
  refreshing,
  stale,
  error,
  runs,
  servingGraph,
  onRefresh,
  className,
}: RoutingGraphImportStatusViewProps) {
  return (
    <section className={cn("border-b border-border bg-background", className)} aria-labelledby="routing-graph-import-status-title">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Platform routing</p>
          <h2 id="routing-graph-import-status-title" className="mt-0.5 text-sm font-black text-foreground">Graph import health</h2>
          <p className="mt-1 text-xs text-muted-foreground">Live worker heartbeats, import progress, and active serving graph.</p>
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={cn("mr-2 h-3.5 w-3.5", refreshing && "animate-spin")} />
          {refreshing ? "Refreshing" : "Refresh"}
        </Button>
      </div>

      {stale ? <div className="flex items-center gap-2 border-y border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs font-medium text-amber-700 dark:text-amber-300"><AlertTriangle className="h-3.5 w-3.5" />Showing the last successful status while the live refresh recovers.</div> : null}
      {error && !stale ? <div className="flex items-center gap-2 border-y border-destructive/30 bg-destructive/10 px-4 py-2 text-xs font-medium text-destructive"><AlertTriangle className="h-3.5 w-3.5" />{error}</div> : null}

      {servingGraph ? (
        <div className="grid gap-3 border-t border-border bg-muted/20 px-4 py-3 text-xs sm:grid-cols-4">
          <div className="flex items-center gap-2"><Database className="h-3.5 w-3.5 text-primary" /><div><p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Serving version</p><p className="font-mono font-bold text-foreground">{servingGraph.graphVersion}</p></div></div>
          <div><p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Regions</p><p className="font-bold text-foreground">{servingGraph.regionsLabel}</p></div>
          <div><p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Promoted</p><p className="font-bold text-foreground">{servingGraph.promotedLabel}</p></div>
          <div><p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Storage</p><p className="font-bold text-foreground">{servingGraph.storageModeLabel}</p></div>
        </div>
      ) : null}

      <div className="border-t border-border">
        {loading && runs.length === 0 ? <p className="px-4 py-5 text-xs text-muted-foreground">Loading routing import status…</p> : null}
        {!loading && runs.length === 0 && !error ? <p className="flex items-center gap-2 px-4 py-5 text-xs text-muted-foreground"><CheckCircle2 className="h-4 w-4" />No recent routing graph imports.</p> : null}
        {runs.map((run) => <RunRow key={run.id} run={run} />)}
      </div>
    </section>
  );
}
